import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import mammoth from "mammoth";
import { FaFileWord } from "react-icons/fa";
import CreatableSelect from "react-select/creatable";
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from "../utils/legacyFontConverter";

const DraftFIR = ({ API_URL }) => {
  const caseTypeOptions = [
    { value: "Abduction", label: "Abduction" },
    { value: "Cheating", label: "Cheating" },
    { value: "Copyright Act", label: "Copyright Violation" },
    { value: "Criminal Trespass", label: "Criminal Trespass" },
    { value: "Cybercrime", label: "Cybercrime" },
    { value: "Dacoity", label: "Dacoity" },
    {
      value: "Prevention of Damage to Public Property Act",
      label: "Damage to Public Property",
    },
    { value: "Dowry Death", label: "Dowry Death" },
    {
      value: "Explosive Substances Act",
      label: "Explosive Substances",
    },
    { value: "Extortion", label: "Extortion" },
    { value: "Gang Rape", label: "Gang Rape" },
    { value: "Grievous Injury", label: "Grievous Injury" },
    {
      value: "Illegal Arms Possession",
      label: "Illegal Arms Possession (Arms Act)",
    },
    {
      value: "Liquor Smuggling (Excise Act)",
      label: "Liquor Smuggling (Excise Act)",
    },
    { value: "Matrimonial", label: "Matrimonial" },
    { value: "Murder", label: "Murder" },
    { value: "Narcotics", label: "Narcotics (NDPS Act)" },
    { value: "POCSO", label: "POCSO" },
    { value: "Rape", label: "Rape" },
    { value: "Road Accident", label: "Road Accident" },
    { value: "Robbery", label: "Robbery" },
    { value: "Simple Injury", label: "Simple Injury" },
    { value: "Theft", label: "Theft" },
    { value: "Trade Marks Act", label: "Trade Mark Violation" },
    {
      value: "Unlawful Activities (UAPA)",
      label: "Unlawful Activities (UAPA)",
    },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showOtherLanguagePopup, setShowOtherLanguagePopup] = useState(false);
  const [language, setLanguage] = useState("");
  const [caseType, setCaseType] = useState("");
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  const [parsingLabel, setParsingLabel] = useState("Reading document...");

  useEffect(() => {
    document.body.style.backgroundColor = "white";
  }, []);

  const handleLanguageChange = (event) => {
    if (event.target.value === 'Other') {
      setShowOtherLanguagePopup(true);
    } else {
      setLanguage(event.target.value);
      setShowOtherLanguagePopup(false);
    }
  };

  const handleCaseTypeChange = (event) => {
    setCaseType(event.target.value);
  };

  const handleLanguageChangePopup = () => {
    setShowOtherLanguagePopup(false);
  };

  const wordFileInputRef = useRef(null);

  const [firText, setFirText] = useState('');
  // Holds the last raw (un-converted) complaint text - either straight from
  // the Word file or straight from the user's typing/pasting - so switching
  // the Legacy Font dropdown re-converts from that original source instead
  // of re-running a conversion on text that may already be converted once.
  const rawFirTextRef = useRef('');
  const [isParsingFile, setIsParsingFile] = useState(false);

  const MAX_WORD_COUNT = 1000;
  const countWords = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };
  const firWordCount = countWords(firText);
  const isOverWordLimit = firWordCount > MAX_WORD_COUNT;
  const [showWordLimitPopup, setShowWordLimitPopup] = useState(false);
  const wasOverWordLimitRef = useRef(false);

  useEffect(() => {
    if (isOverWordLimit && !wasOverWordLimitRef.current) {
      setShowWordLimitPopup(true);
    }
    wasOverWordLimitRef.current = isOverWordLimit;
  }, [isOverWordLimit]);

  const handleWordFileSelected = async (event) => {
    setParsingLabel("Reading Word document...");

    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const isDocx =
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.toLowerCase().endsWith(".docx");

      if (!isDocx) {
        setErrorMessage("Please select a .docx Word document.");
        return;
      }

      setIsParsingFile(true); // 🔹 START LOADER

      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const extractedText = (result?.value || "").trim();

      if (!extractedText) {
        setErrorMessage("No readable text found in the selected Word document.");
        return;
      }

      rawFirTextRef.current = extractedText;
      setFirText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(extractedText, legacyFont) : extractedText);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to read the Word document. Please try another file.");
    } finally {
      setIsParsingFile(false); // 🔹 STOP LOADER
      if (wordFileInputRef.current) wordFileInputRef.current.value = "";
    }
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // dropdown-driven reconversion only fires on a font *change*, so without
  // this a paste (e.g. copying a paragraph out of an old Kruti Dev document)
  // after the font was already selected would silently stay unconverted.
  const handleFirTextPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = firText.slice(0, selectionStart) + convertedPasted + firText.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source. Exact when raw/displayed are still
    // aligned (nothing converted yet, or pasting at the end); approximate
    // otherwise, since conversion can change text length.
    const raw = rawFirTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawFirTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setFirText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const handleWordFileClick = () => {
    setErrorMessage(null);
    wordFileInputRef.current?.click();
  };

  const [complaintResult, setComplaintResult] = useState(null);

  const handleComplaintSubmit = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    var jwt = localStorage.getItem('jwtToken');

    try {
      const response = await axios.post(API_URL + '/complaint', { complaint: firText }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
      });

      setComplaintResult(response.data);
    } catch (error) {
      const defaultErrorMessage = 'An error occurred while submitting the form. Please try again later.';
      setErrorMessage(error.response?.data?.message || error.message || defaultErrorMessage);
      console.error('Error submitting the complaint:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (caseType === "") {
      setErrorMessage("Select the case type.");
      return;
    }
    if (language === "") {
      setErrorMessage("Select the FIR language.");
      return;
    }
    if (firText.trim() === "") {
      setErrorMessage("Enter the FIR contents.");
      return;
    }

    handleComplaintSubmit();
  };

  const handleDraftAnother = () => {
    setComplaintResult(null);
    setFirText("");
    rawFirTextRef.current = "";
    setCaseType("");
    setLanguage("");
    setLegacyFont(LEGACY_FONTS[0]);
  };

  return (
    <div>
      <Navbar />

      <div className="form-container" style={{ marginTop: '100px' }}>
        <div style={{ alignContent: 'center', textAlign: 'center' }}>
          <h2>Draft FIR</h2>
        </div>
        <br></br>

        {complaintResult ? (
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px 40px' }}>
            <h3 style={{ textAlign: 'center' }}>Complaint Analysis</h3>

            {(!complaintResult.allegations || complaintResult.allegations.length === 0) && (
              <p style={{ textAlign: 'center' }}>No allegations were identified in the complaint.</p>
            )}

            {complaintResult.allegations && complaintResult.allegations.map((allegation, index) => {
              const analysis = (complaintResult.allegationAnalysis && complaintResult.allegationAnalysis[allegation]) || {};
              return (
                <div key={index} className="info-section" style={{ marginBottom: '20px' }}>
                  <p><b>{allegation}</b></p>
                  {Object.entries(analysis).map(([key, value]) => (
                    <p key={key} style={{ textAlign: 'justify' }}><b>{key}:</b> {value}</p>
                  ))}
                </div>
              );
            })}

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button className="submit-button" onClick={handleDraftAnother}>Draft Another Complaint</button>
            </div>
          </div>
        ) : (
        <form className="responsive-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="case-type">Case Type</label>

            <CreatableSelect
              inputId="case-type"
              classNamePrefix="upload-select"
              options={caseTypeOptions}
              placeholder="Select or Enter Case Type"
              isClearable
              formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
              value={
                caseType
                  ? {
                    value: caseType,
                    label: caseType,
                  }
                  : null
              }
              onChange={(selectedOption) => {
                const value = selectedOption ? selectedOption.value : "";
                setCaseType(value);

                handleCaseTypeChange({
                  target: {
                    name: "case-type",
                    value,
                  },
                });
              }}
              styles={{
                paddingTop: "10px",
                container: (provided) => ({
                  ...provided,
                  width: "100%",
                  marginTop: "4px",
                }),
                control: (provided) => ({
                  ...provided,
                  minHeight: "34px",
                  height: "38px",
                  fontSize: "14px",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  height: "38px",
                  padding: "0 8px",
                  fontSize: "16px",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  maxWidth: "calc(100% - 10px)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }),
                input: (provided) => ({
                  ...provided,
                  margin: 0,
                  padding: 0,
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: "34px",
                }),
                clearIndicator: (provided) => ({
                  ...provided,
                  padding: "4px",
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  padding: "4px",
                }),
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="document-type">Complaint Language</label>
            <select
              id="document-type"
              name="document-type"
              className="upload-select"
              onChange={handleLanguageChange}
              value={language}
            >
              <option value="" disabled>
                Select Language
              </option>

              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
              <option value="Gujarati">Gujarati</option>
              <option value="Marathi">Marathi</option>
              <option value="Bengali">Bengali</option>
              <option value="Odia">Odia</option>
              <option value="Telugu">Telugu</option>
              <option value="Tamil">Tamil</option>
              <option value="Kannada">Kannada</option>
              <option value="Malayalam">Malayalam</option>
              <option value="Punjabi">Punjabi</option>
              <option value="Urdu">Urdu</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="legacy-font">Legacy Font</label>
            <select
              id="legacy-font"
              name="legacy-font"
              className="upload-select"
              value={legacyFont}
              onChange={(e) => {
                const newFont = e.target.value;
                setLegacyFont(newFont);
                if (isLegacyFontSelected(newFont) && rawFirTextRef.current.trim() !== "") {
                  setFirText(convertLegacyToUnicode(rawFirTextRef.current, newFont));
                } else if (newFont === LEGACY_FONTS[0]) {
                  // Not Applicable - restore the original text as first pasted/extracted, undoing any conversion.
                  setFirText(rawFirTextRef.current);
                }
              }}
              style={{ width: "100%", boxSizing: "border-box", height: "38px", padding: "5px 10px", marginTop: "5px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" }}
            >
              {LEGACY_FONTS.map((font) => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ position: "relative", flex: "1 1 100%", width: "100%" }}>
            <label htmlFor="document">Complaint</label>

            <textarea
              value={firText}
              onChange={(e) => {
                setFirText(e.target.value);
                rawFirTextRef.current = e.target.value;
              }}
              onPaste={handleFirTextPaste}
              disabled={isParsingFile}
              style={{
                width: "100%",
                height: "300px",
                marginTop: "10px",
                fontSize: "16px",
                lineHeight: "1.5",
                resize: "vertical",
                opacity: isParsingFile ? 0.5 : 1,
                boxSizing: "border-box",
              }}
            />

            <p style={{ marginTop: "8px", fontSize: "13px", textAlign: "right", color: isOverWordLimit ? "#d32f2f" : "#666" }}>
              {firWordCount} / {MAX_WORD_COUNT} words
            </p>

            {isParsingFile && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                  borderRadius: "4px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div className="spinner" />
                  <p style={{ marginTop: "8px", fontWeight: 600 }}>
                    {parsingLabel}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* Hidden Word file input */}
            <input
              ref={wordFileInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleWordFileSelected}
            />

            <div style={{ width: "100%" }}>
              {/* Left (Word) + Right (Submit) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "12px",
                }}
              >
                {/* LEFT group */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleWordFileClick}
                    disabled={isLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      background: "white",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                    title="Upload a Word (.docx) file and insert its contents under Complaint:"
                  >
                    <FaFileWord style={{ fontSize: "18px", color: "#0207f9" }} />
                    Word
                  </button>
                </div>

                {/* RIGHT Submit */}
                <button
                  type="submit"
                  className="submit-button"
                  style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", height: "38px" }}
                  disabled={
                    isLoading ||
                    isParsingFile ||
                    isOverWordLimit
                  }
                  hidden={errorMessage}
                >
                  {isLoading ? <div className="spinner" style={{ width: "16px", height: "16px", borderWidth: "2px", margin: "0" }}></div> : "Submit"}
                </button>
              </div>

              <div
                hidden={!isLoading}
                style={{ marginTop: "10px", alignContent: "center", textAlign: "center" }}
              >
                <p style={{ fontSize: "14px" }} hidden={!isLoading}>
                  <strong>
                    Please wait. AI Agent is analyzing the complaint - this may take about a minute
                    depending on the complexity of the complaint.
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </form>
        )}

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                <strong>{errorMessage}</strong>
              </div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{ padding: '10px 20px', fontSize: '14px', color: 'white', backgroundColor: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {showOtherLanguagePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Support for more languages coming soon!</h3>
              <p>In the meantime, please translate your FIR into English using tools like <a href="https://chat.openai.com/" target="_blank" rel="noopener noreferrer">ChatGPT</a> and <a href="https://openpdf.com/lp/translate-pdf.html" target="_blank" rel="noopener noreferrer">OpenPDF</a> and upload the translated FIR as a PDF.</p>
              <p><strong>For assistance, please email us at contact@legalapp.in.</strong></p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={handleLanguageChangePopup}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {showWordLimitPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Word Limit Reached</h3>
              <p style={{ textAlign: "center" }}>
                The free version supports a complaint of up to {MAX_WORD_COUNT} words. Please shorten the text to continue, or contact us for a paid plan with a higher limit.
              </p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => setShowWordLimitPopup(false)}
                >OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default DraftFIR;
